import { Alert, Box } from '@mui/material';
import ButtonCustom from 'components/ButtonCustom';
import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getRevisionByIdWithProject } from 'services/revisions';
import { USER_ROLES } from 'utils/constants';
import { IRevisionWithProjectResponse } from 'utils/contracts';
import ProjectBanner from '../components/ProjectBanner';
import UseAuthContext from '../context/AuthContext';

const Iframe3DViewer: React.FC = () => {
  const { isAuthenticated, auth, organizationId, role } = useContext(UseAuthContext);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const revisionId = searchParams.get('revisionId');
  const projectId = searchParams.get('projectId');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { data: revisionWithProjectData, isLoading } = useQuery<IRevisionWithProjectResponse>(
    ['getRevisionByIdWithProject', revisionId, projectId],
    () => getRevisionByIdWithProject(revisionId, projectId, organizationId),
    {
      // Allow inspectors to access even without organizationId or with different organizationId
      enabled: !!revisionId && !!projectId && (!!organizationId || role === USER_ROLES.INSPECTOR || role === USER_ROLES.INSPECTOR_MANAGER || role === USER_ROLES.ADMIN)
    }
  );

  // Send token to iframe when it loads
  const handleIframeLoad = () => {
    if (iframeRef.current && auth?.token) {
      console.log('Iframe loaded, sending token...');
      
      // Send token to iframe via postMessage
      const sendToken = () => {
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({
            type: 'SET_TOKEN',
            token: auth.token,
            organizationId: organizationId || null,
            projectClimate: revisionWithProjectData?.project_climate || null,
            idfName: revisionWithProjectData?.idf_name || '',
          }, '*');
          console.log('Token sent to iframe');
        }
      };
      
      // Send immediately
      sendToken();
      
      // Also send after a short delay to ensure iframe is ready
      setTimeout(() => {
        sendToken();
      }, 500);
      
    }
  };

  // When revision meta arrives (or updates), push climate for pre-sim EPW auto-load
  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;
    const idfName = revisionWithProjectData?.idf_name || '';
    if (!revisionWithProjectData?.project_climate && !idfName) return;
    iframeRef.current.contentWindow.postMessage({
      type: 'SET_PROJECT_META',
      projectClimate: revisionWithProjectData?.project_climate || null,
      idfName,
    }, '*');
  }, [revisionWithProjectData?.project_climate, revisionWithProjectData?.idf_name]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // More permissive origin check for production
      const allowedOrigins = [
        window.location.origin,
        process.env.REACT_APP_HOST || 'http://localhost:3000'
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('Message from unauthorized origin:', event.origin);
        return;
      }

      if (event.data.type === 'TOKEN_RECEIVED') {
        console.log('Token successfully sent to 3D viewer');
      }
      
      if (event.data.type === 'TOKEN_SET') {
        console.log('Token successfully set in 3D viewer');
      }
      
      if (event.data.type === 'TOKEN_ERROR') {
        console.error('Token error in 3D viewer:', event.data.error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      // Build the current path with all query params for redirect after login
      const currentPath = location.pathname + location.search;
      navigate('/login', { state: { redirect: currentPath } });
    }
  }, [isAuthenticated, navigate, location]);

  // Check if user belongs to the same organization as the revision
  // Allow inspectors and inspector managers to access regardless of organization
  useEffect(() => {
    if (revisionWithProjectData && organizationId && role !== USER_ROLES.ADMIN && role !== USER_ROLES.INSPECTOR && role !== USER_ROLES.INSPECTOR_MANAGER) {
      const revisionOrganizationId = revisionWithProjectData.organization_id;
      
      if (organizationId !== revisionOrganizationId) {
        console.error('Access denied: User does not belong to the same organization as the revision');
      }
    }
  }, [revisionWithProjectData, organizationId, navigate, role]);

  if (!code) {
    return (
      <Box sx={{ p: 2, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="error">
          No revision code provided. Please add ?code=YOUR_CODE to the URL.
        </Alert>
      </Box>
    );
  }

  // Show access denied message if user doesn't belong to the same organization
  // Allow inspectors and inspector managers to access regardless of organization
  if (revisionWithProjectData && organizationId && organizationId !== revisionWithProjectData.organization_id && role !== USER_ROLES.ADMIN && role !== USER_ROLES.INSPECTOR && role !== USER_ROLES.INSPECTOR_MANAGER) {
    return (
      <Box sx={{ p: 2, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <Alert severity="error">
          {t('3D_VIEWER_ACCESS_DENIED')}
        </Alert>
        <ButtonCustom variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
          {t('3D_VIEWER_BUTTON_BACK_TO_PROJECTS')}
        </ButtonCustom>
      </Box>
    );
  }

  // Construct the 3D viewer URL with the code using backend server from environment variable
  const backendHost = process.env.REACT_APP_HOST || 'http://localhost:3000';
  const orgQuery = organizationId ? `&organizationId=${encodeURIComponent(organizationId)}` : '';
  const viewerUrl = `${backendHost}/idf/3d?code=${code}${orgQuery}`;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Project Banner */}
      <ProjectBanner 
        isLoading={isLoading}
        revisionWithProjectData={revisionWithProjectData}
      />

      {/* 3D Viewer iframe */}
      <Box sx={{ flexGrow: 1, marginTop: '80px', overflow: 'hidden' }}>
        <iframe
          ref={iframeRef}
          src={viewerUrl}
          width="100%"
          height="100%"
          title="3D Building Viewer"
          style={{
            border: 'none',
            borderRadius: '0',
            boxShadow: 'none'
          }}
          onLoad={handleIframeLoad}
          onError={(e) => console.error('3D Viewer iframe error:', e)}
        />
      </Box>
    </Box>
  );
};

export default Iframe3DViewer; 